import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ACCEPT = "image/jpeg,image/png,image/heic,image/heif";

/**
 * Photo gallery — thumbnails + drop-zone + lightbox.
 *
 * props:
 *   reportId          — required
 *   photos            — array of PhotoRef objects
 *   workItemId        — undefined for cover gallery, or the work item id
 *   readOnly          — hides add zone + delete X
 *   onChange(nextPhotos) — called after successful upload/delete with the new list
 */
export default function PhotoGallery({
    reportId,
    photos,
    workItemId,
    readOnly = false,
    onChange,
    testIdPrefix = "gallery",
    mobile = false,
}) {
    const [urlMap, setUrlMap] = useState({}); // {photoId: objectUrl}
    const [uploading, setUploading] = useState(0);
    const [lightbox, setLightbox] = useState(null); // photo ref
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const revokeOnUnmount = useRef([]);

    // Fetch thumbs as blobs (Authorization header required)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const next = { ...urlMap };
            for (const p of photos || []) {
                if (next[p.id]) continue;
                try {
                    const { data } = await api.get(
                        `/reports/${reportId}/photos/${p.id}`,
                        { responseType: "blob" }
                    );
                    if (cancelled) return;
                    const url = URL.createObjectURL(data);
                    revokeOnUnmount.current.push(url);
                    next[p.id] = url;
                    setUrlMap((prev) => ({ ...prev, [p.id]: url }));
                } catch (e) {
                    /* fetch failure — leave empty; renders a placeholder */
                }
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [photos, reportId]);

    useEffect(() => {
        return () => {
            revokeOnUnmount.current.forEach((u) => {
                try { URL.revokeObjectURL(u); } catch {}
            });
        };
    }, []);

    const uploadOne = useCallback(
        async (file) => {
            const fd = new FormData();
            fd.append("file", file);
            const qs = workItemId ? `?work_item_id=${encodeURIComponent(workItemId)}` : "";
            try {
                const { data } = await api.post(
                    `/reports/${reportId}/photos${qs}`,
                    fd,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );
                return data.photo;
            } catch (err) {
                const s = err?.response?.status;
                const detail = err?.response?.data?.detail;
                let msg = "Upload failed";
                if (s === 413) msg = "File too large — max 10 MB";
                else if (s === 415) msg = "Unsupported format";
                else if (s === 409) msg = typeof detail === "string" ? detail : "Max 10 photos reached";
                else if (typeof detail === "string") msg = detail;
                toast.error(`${file.name}: ${msg}`, { duration: 2500 });
                return null;
            }
        },
        [reportId, workItemId]
    );

    const handleFiles = useCallback(
        async (files) => {
            const list = Array.from(files || []);
            if (list.length === 0) return;
            setUploading(list.length);
            const created = [];
            for (const f of list) {
                const p = await uploadOne(f);
                if (p) created.push(p);
                setUploading((n) => n - 1);
            }
            if (created.length && onChange) {
                onChange([...(photos || []), ...created]);
            }
        },
        [uploadOne, onChange, photos]
    );

    const onFileInputChange = (e) => {
        handleFiles(e.target.files);
        e.target.value = ""; // allow re-selecting the same file
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
    };

    const deletePhoto = async (photo) => {
        try {
            await api.delete(`/reports/${reportId}/photos/${photo.id}`);
            const next = (photos || []).filter((p) => p.id !== photo.id);
            if (onChange) onChange(next);
        } catch (err) {
            toast.error("Couldn't delete photo", { duration: 2000 });
        }
    };

    const empty = (photos || []).length === 0;

    const gridCols = mobile ? "grid-cols-3" : "grid-cols-2 md:grid-cols-4";
    const gridGap = mobile ? "gap-1.5" : "gap-2";

    return (
        <div data-testid={testIdPrefix}>
            {!empty && (
                <div className={`grid ${gridCols} ${gridGap} mb-3`}>
                    {(photos || []).map((p) => (
                        <div
                            key={p.id}
                            className="relative aspect-square rounded-[12px] overflow-hidden border border-[rgba(15,23,42,0.08)] bg-slate-50 cursor-pointer group"
                            onClick={() => setLightbox(p)}
                            data-testid={`${testIdPrefix}-thumb-${p.id}`}
                        >
                            {urlMap[p.id] ? (
                                <img
                                    src={urlMap[p.id]}
                                    alt={p.filename}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-[11px] text-slate-400">
                                    Loading…
                                </div>
                            )}
                            {!readOnly && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        deletePhoto(p);
                                    }}
                                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/95 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                    aria-label="Delete photo"
                                    data-testid={`${testIdPrefix}-delete-${p.id}`}
                                >
                                    <X size={12} strokeWidth={2.5} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {!readOnly && mobile && (
                <div className="flex flex-col gap-2" data-testid={`${testIdPrefix}-mobile-actions`}>
                    <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={onFileInputChange}
                        data-testid={`${testIdPrefix}-camera-input`}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={ACCEPT}
                        className="hidden"
                        onChange={onFileInputChange}
                        data-testid={`${testIdPrefix}-file-input`}
                    />
                    <button
                        type="button"
                        onClick={() => cameraInputRef.current?.click()}
                        className="btn btn-primary w-full"
                        data-testid={`${testIdPrefix}-take-photo`}
                    >
                        <Plus size={16} strokeWidth={2.5} />
                        Take photo
                    </button>
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="btn btn-secondary w-full"
                        data-testid={`${testIdPrefix}-choose-library`}
                    >
                        Choose from library
                    </button>
                    <div className="text-[11px] text-slate-400 text-center">
                        {uploading > 0 ? `Uploading ${uploading}…` : "JPG, PNG, HEIC · max 10 MB · up to 10"}
                    </div>
                </div>
            )}

            {!readOnly && !mobile && (
                <label
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    className={`block cursor-pointer border-2 border-dashed rounded-[12px] px-4 py-6 text-center text-[13px] transition-colors ${
                        dragOver
                            ? "border-emerald-400 bg-emerald-50/40 text-emerald-800"
                            : "border-slate-300 bg-white/40 text-slate-500 hover:border-slate-400"
                    }`}
                    data-testid={`${testIdPrefix}-dropzone`}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={ACCEPT}
                        className="hidden"
                        onChange={onFileInputChange}
                        data-testid={`${testIdPrefix}-file-input`}
                    />
                    <div className="flex items-center justify-center gap-2">
                        <Plus size={14} strokeWidth={2} />
                        {uploading > 0
                            ? `Uploading ${uploading}…`
                            : empty
                            ? "Add photos"
                            : "Add more photos"}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1">
                        JPG, PNG, HEIC · max 10 MB · up to 10 photos
                    </div>
                </label>
            )}

            {readOnly && empty && (
                <div className="text-[13px] text-slate-400 italic">No photos.</div>
            )}

            {lightbox && (
                <div
                    className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
                    onClick={() => setLightbox(null)}
                    role="dialog"
                    data-testid={`${testIdPrefix}-lightbox`}
                >
                    <button
                        type="button"
                        onClick={() => setLightbox(null)}
                        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                    {urlMap[lightbox.id] && (
                        <img
                            src={urlMap[lightbox.id]}
                            alt={lightbox.filename}
                            className="max-h-full max-w-full object-contain shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
